import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import useRect from 'hooks/common/useRect';
import React from 'react';
import { promise, sequence, sleep } from 'utils/asynchrous';
import { lerp } from 'utils/number';

const steps = 50;
const iteration = Array.from({ length: steps + 1 }).map((_, index) => index);

export default function Ratiobar(props) {
  const {
    duration,
    limits,
    values,
    colors,
    maxHeight,
    ...rest
  } = props;

  const ms = duration ? 1000 : 0;

  const [lastLimits, setLastLimits] = React.useState(() => limits);
  const [lastValues, setLastValues] = React.useState(() => values);
  const [currentLimits, setCurrentLimits] = React.useState(() => limits);
  const [currentValues, setCurrentValues] = React.useState(() => values);
  const [total, setTotal] = React.useState(() => limits.reduce((a, c) => a + c, 0) || 1);

  const ref = React.useRef();
  const rect = useRect(ref);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      // console.log(limits, values);
      sequence(iteration, promise(async (i, resolve, reject) => {
        if (ms) {
          await sleep(ms / steps / 2);
        }

        const vh = lerp(lastValues[0], values[0], i * (1 / steps));
        const vl = lerp(lastValues[1], values[1], i * (1 / steps));
        setCurrentValues(() => [vh, vl]);

        const lh = lerp(lastLimits[0], limits[0], i * (1 / steps));
        const ll = lerp(lastLimits[1], limits[1], i * (1 / steps));
        setCurrentLimits(() => [lh, ll]);
        setTotal(() => Math.max(1, lh + ll, vh + vl));

        // console.log(vh, vl, lh, ll);

        resolve();
      }), () => {
        setLastLimits(() => limits);
        setLastValues(() => values);
      });
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [limits[0], limits[1], values[0], values[1]]);

  const rendered = React.useMemo(() => {
    // console.log('rendered', currentValues, total, (currentValues[0] * 100 / total).toFixed(0));
    return (
      <VBox
        full
        {...ex('Ratiobar', rest)}
        {...{
          ref,
          'data-total': total,
          'data-height': rect.height,
        }}
      >
        <HBox>
          <div>
            {(currentValues[0] * 100 / total).toFixed(0)}
          </div>
          <div {...ex('Unit')}>
            %
          </div>
        </HBox>
        <div
          {...{
            style: {
              minHeight: `${Math.min(maxHeight, (currentLimits[0]) * rect.height * 0.6 / total)}px`,
            }
          }}
        />
        <div>
          <div {...{
            style: {
              backgroundColor: colors[0],
              minHeight: `${Math.min(maxHeight, currentValues[0] * rect.height * 0.6 / total)}px`,
            }
          }}
          >
            {(+currentValues[0].toFixed(0)).toString()}
          </div>
        </div>
        <div />
        <div>
          <div {...{
            style: {
              backgroundColor: colors[1],
              minHeight: `${Math.min(maxHeight, currentValues[1] * rect.height * 0.6 / total)}px`,
            }
          }}
          >
            {(+currentValues[1].toFixed(0)).toString()}
          </div>
        </div>
        <div
          {...{
            style: {
              minHeight: `${Math.min(maxHeight, (currentLimits[1]) * rect.height * 0.6 / total)}px`,
            }
          }}
        />
        <HBox>
          <div>
            {(currentValues[1] * 100 / total).toFixed(0)}
          </div>
          <div {...ex('Unit')}>
            %
          </div>
        </HBox>
      </VBox>
    );
  }, [colors, currentValues, rect.height, rest, total]);

  return rendered;
}
